import { Client } from '../types';
import { useClientEdit } from '../hooks/useClientEdit';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { updateClient } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface ClientEmail {
  id?: number;
  client_id: number;
  email: string;
  is_primary: boolean;
  email_type?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface Language {
  id: string;
  name: string;
  code?: string;
  active?: boolean;
}

interface ClientInfoTabProps {
  client: Client | null;
  clientId?: string;
  onClientUpdate?: (updatedClient: Client) => void;
}

interface DriverLicenseEntry {
  code: string;
  dateOfFirstIssue: string;
  expiresOn: string;
}
interface Consultant {
  id: string;
  name: string;
  surname: string;
  id_passport_number?: string;
  contact_number?: string;
  email?: string;
  active?: boolean;
  verified?: boolean;
  start_date?: string;
  code?: string;
  work_model?: string;
  position?: string;
  role?: string;
}

const ClientInfoTab = ({ client, clientId, onClientUpdate }: ClientInfoTabProps) => {
  const { can } = usePermissions();
  const { editedClient, errors, handleSubmit, handleInputChange, reset, updateClientField, } = useClientEdit(client, clientId);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [displayClient, setDisplayClient] = useState<Client | null>(client);
  const [mobileNumber, setMobileNumber] = useState(editedClient?.mobile_number?.replace('+27', '') || '');
  // STATE FOR DRIVER LICENSES
  const [driverLicenses, setDriverLicenses] = useState<DriverLicenseEntry[]>([]);
  const [availableLicenseCodes] = useState([
    'A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'EB', 'EC', 'EC1', 'ED', 'ED1'
  ]);

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [isLoadingConsultants, setIsLoadingConsultants] = useState(false);

  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);

  const [clientEmails, setClientEmails] = useState<ClientEmail[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Email management functions
const addEmail = async () => {
  const trimmedEmail = emailInput.trim().toLowerCase();
  
  if (!trimmedEmail) {
    setEmailError('Email cannot be empty');
    return;
  }
  
  if (!validateEmail(trimmedEmail)) {
    setEmailError('Please enter a valid email address');
    return;
  }
  
  // Check for duplicates
  if (clientEmails.some(email => email.email === trimmedEmail)) {
    setEmailError('Email already added');
    return;
  }
  
  // Check if there's already a primary email
  const hasPrimary = clientEmails.some(email => email.is_primary);
  
  // If there's already a primary, this new email should NOT be primary
  const isPrimary = !hasPrimary;
  
  const newEmail: ClientEmail = {
    client_id: client?.id ? parseInt(client.id.toString()) : 0,
    email: trimmedEmail,
    is_primary: isPrimary,
    email_type: 'personal'
  };
  
  // If we're in edit mode and this is going to be primary,
  // we need to update the existing primary to not be primary first
  if (isEditing && isPrimary && hasPrimary) {
    // Find the current primary email
    const currentPrimary = clientEmails.find(email => email.is_primary);
    
    if (currentPrimary) {
      // Update the UI to show the current primary as no longer primary
      setClientEmails(prev => 
        prev.map(email => 
          email.is_primary ? { ...email, is_primary: false } : email
        )
      );
    }
  }
  
  setClientEmails(prev => [...prev, newEmail]);
  setEmailInput('');
  setEmailError(null);
  
  // Update the editedClient's email field for backward compatibility
  if (isPrimary && editedClient) {
    updateClientField('email', trimmedEmail);
  }
};

const removeEmail = async (index: number) => {
  const emailToRemove = clientEmails[index];
  
  // If email has an ID, delete it from the database
  if (emailToRemove.id) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      console.log('🗑️ Deleting email from database:', emailToRemove.email);
      
      // Call DELETE API
      const response = await fetch(
  `${API_URL}/api/client-emails/${emailToRemove.id}`,
  {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }
);

      if (response.ok) {
        console.log('✅ Email deleted from database');
        
        // Remove from local state
        setClientEmails(prev => {
          const newEmails = prev.filter((_, i) => i !== index);
          
          // If we removed the primary email and there are other emails, make the first one primary
          if (emailToRemove.is_primary && newEmails.length > 0) {
            newEmails[0].is_primary = true;
            // Update editedClient email for backward compatibility
            updateClientField('email', newEmails[0].email);
            
            // Update this new primary in the database
            setTimeout(() => {
              // Find the index of the new primary email and call setPrimaryEmail
              const newPrimaryIndex = newEmails.findIndex(e => e.is_primary);
              if (newPrimaryIndex !== -1) {
                setPrimaryEmail(newPrimaryIndex);
              }
            }, 100);
          }
          
          // If no emails left, clear the email field
          if (newEmails.length === 0) {
            updateClientField('email', '');
          }
          
          return newEmails;
        });
        
        toast.success('Email deleted successfully', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to delete email:', errorText);
        toast.error('Failed to delete email', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('❌ Error deleting email:', error);
      toast.error('Error deleting email', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  } else {
    // If no ID (newly added email), just remove from local state
    setClientEmails(prev => {
      const newEmails = prev.filter((_, i) => i !== index);
      
      // If we removed the primary email and there are other emails, make the first one primary
      if (emailToRemove.is_primary && newEmails.length > 0) {
        newEmails[0].is_primary = true;
        updateClientField('email', newEmails[0].email);
      }
      
      // If no emails left, clear the email field
      if (newEmails.length === 0) {
        updateClientField('email', '');
      }
      
      return newEmails;
    });
  }
};

const setPrimaryEmail = async (index: number) => {
  console.log('🔧 setPrimaryEmail called with index:', index);
  console.log('🔧 Is editing mode?', isEditing);
  
  if (!client?.id) {
    console.error('❌ No client ID available');
    return;
  }

  const emailToSet = clientEmails?.[index];
  if (!emailToSet) {
    console.error('❌ Email not found at index:', index);
    return;
  }

  if (!emailToSet.id) {
    console.error('❌ Cannot set unsaved email as primary. Please save the client first.');
    toast.error('Please save the client first before setting primary email', {
      position: 'top-right',
      autoClose: 3000,
    });
    return;
  }

  // Optimistically update UI
  const updatedEmails = clientEmails?.map((email, i) => ({
    ...email,
    is_primary: i === index
  })) || [];
  setClientEmails(updatedEmails);

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    console.log('🔧 Setting email as primary in database:', emailToSet.id);
    
    // Single API call to set as primary - backend handles clearing existing primary
    const response = await fetch(
  `${API_URL}/api/client-emails/${emailToSet.id}/primary`,
  {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }
);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to set primary email:', errorText);
      throw new Error(`Failed to set primary email: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Primary email set successfully:', result);
    
    // Update the editedClient's email field for backward compatibility
    updateClientField('email', emailToSet.email);
    
    toast.success('Primary email updated successfully', {
      position: 'top-right',
      autoClose: 3000,
    });
    
    // Refresh emails from server to ensure sync
    await fetchClientEmails();
    
  } catch (error) {
    console.error('❌ Failed to set primary email:', error);
    // Revert optimistic update on error
    await fetchClientEmails();
    
    toast.error('Failed to update primary email', {
      position: 'top-right',
      autoClose: 3000,
    });
  }
};

const handleEmailKeyDown = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault();
    addEmail();
  }
};

// Function to fetch client emails
const fetchClientEmails = async () => {
  console.log('📧 [1] fetchClientEmails STARTED');
  
  if (!clientId) {
    console.error('📧 [2] No clientId!', clientId);
    return;
  }
  
  console.log('📧 [3] Client ID:', clientId);
  
  try {
    const token = localStorage.getItem('token');
    console.log('📧 [4] Token exists?', !!token);
    
    if (!token) {
      console.error('📧 [5] No token found');
      return;
    }
    
    // 🔥 Keep cache-busting timestamp but remove custom headers
    const timestamp = Date.now();
    const url = `${API_URL}/api/client-emails/${clientId}?_t=${timestamp}`;
    console.log('📧 [6] Fetching from:', url);
    console.log('📧 DEBUG: Cache-busting timestamp:', timestamp);
    
    const response = await fetch(url, {
  headers: {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
  cache: 'no-store',
});
    
    console.log('📧 [7] Response status:', response.status);
    
    if (response.ok) {
      const text = await response.text();
      console.log('📧 [8] Response text:', text);
      
      try {
        const data = JSON.parse(text);
        console.log('📧 [9] Parsed data:', data);
        
        if (data.success && Array.isArray(data.data)) {
          console.log('📧 [10] Setting clientEmails, count:', data.data.length);
          const primaryEmail = data.data.find((e: ClientEmail) => e.is_primary);
          console.log('📧 [11] Primary email from API:', primaryEmail?.email);
          console.log('📧 [12] All emails:', data.data.map((e: ClientEmail) => 
            `${e.email} (primary=${e.is_primary}, updated=${e.updated_at})`
          ));
          setClientEmails(data.data);
        } else {
          console.error('📧 [13] Invalid response structure:', data);
        }
      } catch (parseError) {
        console.error('📧 [14] JSON parse error:', parseError);
      }
    } else {
      const errorText = await response.text();
      console.error('📧 [15] Response not OK:', errorText);
    }
  } catch (error) {
    console.error('📧 [16] fetchClientEmails error:', error);
  }
  
  console.log('📧 [17] fetchClientEmails COMPLETED');
};

  // Fetch consultants when component mounts
  const fetchConsultants = async () => {
    setIsLoadingConsultants(true);
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Token from localStorage:', token ? 'Exists' : 'Missing');

      if (!token) {
        console.error('❌ No authentication token found');
        toast.error('Please log in again', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      const fullUrl = `${API_URL}/api/consultants`;
      console.log('🔍 Fetching from:', fullUrl);
      
      const response = await fetch(fullUrl, {
  method: 'GET',
  headers: {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
  credentials: 'include',
});

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      console.log('🔍 Raw response text:', text);

      // Parse the JSON
      const data = JSON.parse(text);
      console.log('🔍 Parsed consultants data:', data);
      
      if (data.success && Array.isArray(data.data)) {
        setConsultants(data.data);
        console.log('✅ Consultants loaded successfully:', data.data.length);
      } else {
        console.error('Unexpected API response structure:', data);
        toast.error('Failed to load consultants: Invalid response format', {
          position: 'top-right',
          autoClose: 3000,
        });
      }

    } catch (error: any) {
      console.error('❌ Error fetching consultants:', error);
      
      // More specific error messages
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        toast.error('Network error: Cannot connect to server', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else if (error.message.includes('401')) {
        toast.error('Authentication failed. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error('Failed to load consultants. Check console for details.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoadingConsultants(false);
    }
  };

  // function to fetch languages
const fetchLanguages = async () => {
  setIsLoadingLanguages(true);
  try {
    const token = localStorage.getItem('token');
    console.log('🔍 Fetching languages...');

    if (!token) {
      console.error('❌ No authentication token found');
      toast.error('Please log in again', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const fullUrl = `${API_URL}/api/languages`;
    console.log('🔍 Fetching languages from:', fullUrl);
    
    const response = await fetch(fullUrl, {
  method: 'GET',
  headers: {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
  credentials: 'include',
});

    console.log('🔍 Languages response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const text = await response.text();
    console.log('🔍 Raw languages response text:', text);

    // Parse the JSON
    const data = JSON.parse(text);
    console.log('🔍 Parsed languages data:', data);
    
    if (data.success && Array.isArray(data.data)) {
      setLanguages(data.data);
      console.log('✅ Languages loaded successfully:', data.data.length);
    } else {
      console.error('Unexpected API response structure for languages:', data);
      toast.error('Failed to load languages: Invalid response format', {
        position: 'top-right',
        autoClose: 3000,
      });
    }

  } catch (error: any) {
    console.error('❌ Error fetching languages:', error);
    
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      toast.error('Network error: Cannot connect to server', {
        position: 'top-right',
        autoClose: 3000,
      });
    } else if (error.message.includes('401')) {
      toast.error('Authentication failed. Please log in again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } else {
      toast.error('Failed to load languages. Check console for details.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  } finally {
    setIsLoadingLanguages(false);
  }
};

useEffect(() => {
  fetchConsultants();
  fetchLanguages();
}, []);

  // Sync displayClient with client prop when it changes
  useEffect(() => {
  setDisplayClient(client);
  
  // Extract just the digits after country code for display
  const phoneValue = client?.phone || client?.mobile_number; // Check both fields
  
  if (phoneValue) {
    let cleanNumber = phoneValue.replace(/\D/g, ''); // Remove all non-digits first
    
    // Remove country code prefixes if they exist
    if (cleanNumber.startsWith('27') && cleanNumber.length > 2) {
      cleanNumber = cleanNumber.substring(2); // Remove '27'
    } else if (cleanNumber.startsWith('20') && cleanNumber.length > 2) {
      cleanNumber = cleanNumber.substring(2); // Remove '20'
    }
    
    setMobileNumber(cleanNumber);
  } else {
    setMobileNumber('');
  }

  // Fetch client emails when client data loads
  if (clientId) {
    fetchClientEmails();
  }
}, [client, clientId]);

  // Initialize driver licenses when client data loads
  useEffect(() => {
    if (client?.drivers_license_codes) {
      const parsedLicenses = parseDriverLicenses(client.drivers_license_codes);
      setDriverLicenses(parsedLicenses);
    } else {
      setDriverLicenses([]);
    }
  }, [client]);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Format date for HTML input (YYYY-MM-DD format)
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as YYYY-MM-DD for HTML date input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  // Parse driver licenses from string format
  const parseDriverLicenses = (licenseString: string): DriverLicenseEntry[] => {
    if (!licenseString || licenseString.trim() === '') {
      return [];
    }

    try {
      // Try to parse as JSON first
      if (licenseString.startsWith('[') || licenseString.startsWith('{')) {
        const parsed = JSON.parse(licenseString);
        if (Array.isArray(parsed)) {
          return parsed.map((license: any) => ({
            code: license.code || license,
            dateOfFirstIssue: license.dateOfFirstIssue || license.date || '',
            expiresOn: license.expiresOn || ''
          }));
        }
      }

      // Parse as pipe-separated format (code|date,code|date)
      const licensePairs = licenseString.split(',');
      return licensePairs.map(pair => {
        const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
        return {
          code: code || '',
          dateOfFirstIssue: dateOfFirstIssue || '',
          expiresOn: expiresOn || ''
        };
      }).filter(license => license.code);

    } catch (error) {
      console.error('Error parsing driver licenses:', error);
      // Fallback: treat as comma-separated codes without dates
      return licenseString.split(',').map(code => ({
        code: code.trim(),
        dateOfFirstIssue: '',
        expiresOn: ''
      })).filter(license => license.code);
    }
  };

  // Driver license management functions
  const addDriverLicense = () => {
    const currentSelectedCodes = driverLicenses.map(license => license.code).filter(code => code !== '');
    const availableCode = availableLicenseCodes.find(code => !currentSelectedCodes.includes(code)) || '';
    
    setDriverLicenses(prev => [
      ...prev,
      { code: availableCode, dateOfFirstIssue: '', expiresOn: '' }
    ]);
  };

  const updateDriverLicense = (index: number, field: keyof DriverLicenseEntry, value: string) => {
    setDriverLicenses(prev => 
      prev.map((license, i) => 
        i === index ? { ...license, [field]: value } : license
      )
    );
  };

  // Convert driver licenses to string format for submission
  const licensesToString = (licenses: DriverLicenseEntry[]): string => {
  const validLicenses = licenses.filter(license => license.code);
    if (validLicenses.length === 0) return '';
    
    const result = validLicenses.map(license => {
      const licenseString = license.dateOfFirstIssue && license.expiresOn
        ? `${license.code}|${license.dateOfFirstIssue}|${license.expiresOn}`
        : license.dateOfFirstIssue
          ? `${license.code}|${license.dateOfFirstIssue}`
          : license.code;
      
      console.log('🔍 License conversion:', {
        code: license.code,
        dateOfFirstIssue: license.dateOfFirstIssue,
        expiresOn: license.expiresOn,
        result: licenseString
      });
      
      return licenseString;
    }).join(',');
    
    console.log('🔍 Final licenses string:', result);
    return result;
  };

  const parseAndFormatDriverLicenses = (licenseString: string | undefined) => {
    if (!licenseString || licenseString.trim() === '') {
      return [];
    }

    try {
      // Try to parse as JSON first (for newer format)
      if (licenseString.startsWith('[') || licenseString.startsWith('{')) {
        const parsed = JSON.parse(licenseString);
        if (Array.isArray(parsed)) {
          return parsed.map((license: any) => ({
            code: license.code || license,
            dateOfFirstIssue: license.dateOfFirstIssue || license.date || '',
            expiresOn: license.expiresOn || ''
          }));
        }
      }

      // Parse as pipe-separated format (code|date,code|date)
      const licensePairs = licenseString.split(',');
      return licensePairs.map(pair => {
        const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
        return {
          code: code || '',
          dateOfFirstIssue: dateOfFirstIssue || '',
          expiresOn: expiresOn || ''
        };
      }).filter(license => license.code); // Filter out empty codes

    } catch (error) {
      console.error('Error parsing driver licenses:', error);
      // Fallback: treat as comma-separated codes without dates
      return licenseString.split(',').map(code => ({
        code: code.trim(),
        dateOfFirstIssue: '',
        expiresOn: ''
      })).filter(license => license.code);
    }
  };

  const formatLicenseDisplay = (licenseString: string | undefined) => {
    const licenses = parseAndFormatDriverLicenses(licenseString);
    
    if (licenses.length === 0) {
      return 'N/A';
    }

    return licenses.map(license => {
      const parts = [];
      
      if (license.code) {
        parts.push(license.code);
      }
      
      if (license.dateOfFirstIssue) {
        const issueDate = new Date(license.dateOfFirstIssue);
        const formattedIssueDate = issueDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        parts.push(`Issued: ${formattedIssueDate}`);
      }
      
      if (license.expiresOn) {
        const expiryDate = new Date(license.expiresOn);
        const formattedExpiryDate = expiryDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        parts.push(`Expires: ${formattedExpiryDate}`);
      }
      
      return parts.join(' - ');
    }).join(', ');
  };

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  
  // Only allow digits and limit to 10 characters
  const cleanNumber = value.replace(/\D/g, '').slice(0, 10);
  
  setMobileNumber(cleanNumber);
  
  // ALWAYS use +27 for South African numbers
  const countryCode = '+27';
  
  // Only set the full number if we have digits
  const fullNumber = cleanNumber ? `${countryCode}${cleanNumber}` : '';
  
  handleInputChange({
    target: {
      name: 'phone',
      value: fullNumber,
    },
  } as React.ChangeEvent<HTMLInputElement>);
};

const validateMobileNumber = (number: string): string | null => {
  
  // If empty, no error (field is optional)
  if (!number || number.length === 0) {
    return null;
  }
  
  // Allow 8-10 digits to handle various existing data formats
  if (number.length < 8 || number.length > 10) {
    return 'Mobile number must be 8-10 digits';
  }
  
  if (!number.match(/^[0-9]{8,10}$/)) {
    return 'Mobile number must contain only digits';
  }
  
  return null;
};

  const handleSubmitWithUpdate = async (e: React.FormEvent) => {
  e.preventDefault();

  console.log('🔍 DEBUG: Starting handleSubmitWithUpdate');
  console.log('🔍 clientId:', clientId);
  console.log('🔍 fetchClientEmails function exists?', typeof fetchClientEmails === 'function');

  // Validate emails
  if (clientEmails.length === 0) {
    toast.error('Please add at least one email address', {
      position: 'top-right',
      autoClose: 3000,
    });
    return;
  }

  // Validate all emails
  for (const email of clientEmails) {
    if (!validateEmail(email.email)) {
      toast.error(`Invalid email format: ${email.email}`, {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
  }

  // Only validate mobile number if it's not empty
  if (mobileNumber.trim() !== '') {
    const mobileError = validateMobileNumber(mobileNumber);
    if (mobileError) {
      toast.error(mobileError, {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
  }

  if (!clientId) {
    toast.error('Cannot save: Client ID is missing.', {
      position: 'top-right',
      autoClose: 3000,
    });
    return;
  }

  try {
    // Convert driver licenses to string format for submission
    const driverLicensesString = licensesToString(driverLicenses);
    
    console.log('🚀 FINAL SOLUTION: Using direct API call');
    console.log('📋 Driver licenses to save:', driverLicensesString);
    console.log('📋 Emails to save:', clientEmails);

    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token');

    console.log('🎯 STEP 1: Saving client record first...');

    // Find the primary email
    const primaryEmail = clientEmails.find(email => email.is_primary);
    console.log('🎯 Primary email in local state:', primaryEmail?.email);

    // Build the complete client data to send
    const clientDataToSend = {
      name: editedClient?.name || '',
      surname: editedClient?.surname || '',
      title: editedClient?.title || 'Mr',
      mobile_number: editedClient?.phone || '',
      middle_names: editedClient?.middle_names || '',
      nickname: editedClient?.nickname || '',
      date_of_birth: editedClient?.date_of_birth || '',
      id_passport_number: editedClient?.id_number || editedClient?.id_passport_number || '',
      telephone_number: editedClient?.telephone_number || '',
      fax_number: editedClient?.fax_number || '',
      marital_status: editedClient?.marital_status || '',
      status: editedClient?.status || '',
      gender: editedClient?.gender || '',
      language: editedClient?.language || '',
      drivers_license_codes: driverLicensesString,
      employer_name: editedClient?.employer_name || '',
      tax_number: editedClient?.tax_number || '',
      job_description: editedClient?.job_description || '',
      consultant_id: editedClient?.consultant || '',
      personal: editedClient?.personal || '',
      // Include the primary email in the client record
      email: primaryEmail?.email || '',
    };

    console.log('📤 Sending client data to server:', clientDataToSend);

    // Make the API call directly
    const updatedClient = await updateClient(clientId, clientDataToSend, token);

    console.log('✅ SUCCESS - Client updated:', updatedClient);

    console.log('🎯 STEP 2: Now saving emails...');

    // Now save all emails
    const emailPromises = clientEmails.map(async (email) => {
      // Check if this email already has an ID (already in database)
      if (email.id) {
        // Update existing email
        const response = await fetch(
  `${API_URL}/api/client-emails/${email.id}`,
  {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      is_primary: email.is_primary,
      email_type: email.email_type || 'personal',
      is_active: true,
    }),
  }
);

        return response.json();
      } else {
        // Add new email
        const response = await fetch(
  `${API_URL}/api/client-emails/${clientId}`,
  {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: email.email,
      is_primary: email.is_primary,
      email_type: email.email_type || 'personal',
    }),
  }
);

        return response.json();
      }
    });

    await Promise.all(emailPromises);
    console.log('✅ All emails saved successfully');

    console.log('🎯 STEP 3: Refreshing emails...');

    // Wait a bit before fetching emails to ensure database consistency
    await new Promise(resolve => setTimeout(resolve, 500));

    // Force a fresh fetch by adding a timestamp to prevent caching
    await fetchClientEmails();
    console.log('✅ fetchClientEmails completed successfully');

    const normalizedClient = {
      ...updatedClient,
      phone: updatedClient.mobile_number || updatedClient.phone
    };

    setDisplayClient(normalizedClient);
    
    if (normalizedClient.phone) {
      let cleanNumber = normalizedClient.phone.replace(/\D/g, '');
      
      if (cleanNumber.startsWith('27') && cleanNumber.length > 2) {
        cleanNumber = cleanNumber.substring(2);
      } else if (cleanNumber.startsWith('20') && cleanNumber.length > 2) {
        cleanNumber = cleanNumber.substring(2);
      }
      
      setMobileNumber(cleanNumber);
    } else {
      setMobileNumber('');
    }

    if (onClientUpdate) {
      onClientUpdate(normalizedClient);
    }
    
    // Update driver licenses state from the response
    if (updatedClient.drivers_license_codes) {
      const parsedLicenses = parseDriverLicenses(updatedClient.drivers_license_codes);
      setDriverLicenses(parsedLicenses);
    }

    setIsEditing(false);
    setIsSaved(true);
    
    toast.success('Client information saved successfully!', {
      position: 'top-right',
      autoClose: 3000,
    });

  } catch (error: any) {
    console.error('❌ FINAL - Error saving client:', error);
    console.error('❌ Error response:', error.response?.data);
    
    toast.error('Failed to save client information.', {
      position: 'top-right',
      autoClose: 3000,
    });
  }
};

  const handleCancel = () => {
  reset();
  setIsEditing(false);

  // 🔥 Clear emails first, then fetch fresh data
  setClientEmails([]);

  // Small delay to ensure state is cleared
  setTimeout(() => {
    if (clientId) {
      fetchClientEmails();
    }
  }, 100);

  // Reset emails
  if (clientId) {
    fetchClientEmails();
  }
  
  // Reset to the original client's phone number
  if (client?.phone) { // ← CHANGE FROM mobile_number TO phone
    let cleanNumber = client.phone.replace(/\D/g, ''); // Remove all non-digits first
    
    // Remove country code prefixes if they exist
    if (cleanNumber.startsWith('27') && cleanNumber.length > 2) {
      cleanNumber = cleanNumber.substring(2); // Remove '27'
    } else if (cleanNumber.startsWith('20') && cleanNumber.length > 2) {
      cleanNumber = cleanNumber.substring(2); // Remove '20'
    }
    
    setMobileNumber(cleanNumber);
  } else {
    setMobileNumber('');
  }

  // Clear email input
  setEmailInput('');
  setEmailError(null);
};

  const removeDriverLicense = (index: number) => {
  setDriverLicenses(prev => prev.filter((_, i) => i !== index));
};

// Get available license codes for a specific dropdown
const getAvailableLicenseCodes = (currentIndex: number) => {
  const currentSelectedCodes = driverLicenses.map(license => license.code).filter(code => code !== '');
  
  return availableLicenseCodes.filter(code => {
    const selectedInOtherDropdowns = currentSelectedCodes.filter(
      (selectedCode, index) => index !== currentIndex && selectedCode === code
    );
    return selectedInOtherDropdowns.length === 0;
  });
};

  // Check if more licenses can be added
const canAddMoreLicenses = () => {
  const availableCodes = getAvailableLicenseCodes(driverLicenses.length);
  return availableCodes.length > 0;
};

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Client Information</h3>
        {isEditing ? (
          <div className="space-x-2">
            {can('client_info', 'write') && (
              <button
                type="submit"
                onClick={handleSubmitWithUpdate}
                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm"
              >
                Save
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
            >
              Cancel
            </button>
          </div>
        ) : !isSaved && can('client_info', 'write') ? (
          <button
            type="button"
            onClick={() => {
              setIsEditing(true);
            }}
            className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 text-sm"
          >
            Edit
          </button>
        ) : null}
      </div>
      <form onSubmit={handleSubmitWithUpdate}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Title and Driver's License - Side by Side */}
            <div className="lg:col-span-1">
              <p className="text-sm font-medium text-gray-700">Title</p>
              {isEditing ? (
                <select
                  name="title"
                  value={editedClient?.title || ''}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  required
                >
                  <option value="">Select Title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
              ) : (
                <p className="text-sm text-gray-900">{displayClient?.title || 'N/A'}</p>
              )}
              {errors.title && isEditing && (
                <p className="text-red-500 text-xs mt-1">{errors.title}</p>
              )}
            </div>

          <div>
            <p className="text-sm font-medium text-gray-700">First Name</p>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={editedClient?.name || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.name || 'N/A'}</p>
            )}
            {errors.name && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Middle Name/s</p>
            {isEditing ? (
              <input
                type="text"
                name="middle_names"
                value={editedClient?.middle_names || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.middle_names ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.middle_names || 'N/A'}</p>
            )}
            {errors.middle_names && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.middle_names}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Surname</p>
            {isEditing ? (
              <input
                type="text"
                name="surname"
                value={editedClient?.surname || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.surname ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.surname || 'N/A'}</p>
            )}
            {errors.surname && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.surname}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Nickname/s</p>
            {isEditing ? (
              <input
                type="text"
                name="nickname"
                value={editedClient?.nickname || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.nickname ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.nickname || 'N/A'}</p>
            )}
            {errors.nickname && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.nickname}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Date of Birth</p>
            {isEditing ? (
              <input
                type="date"
                name="date_of_birth"
                value={formatDateForInput(editedClient?.date_of_birth) || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.date_of_birth ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">
                {displayClient?.date_of_birth 
                  ? new Date(displayClient.date_of_birth).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })
                  : 'N/A'
                }
              </p>
            )}
            {errors.date_of_birth && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Gender</p>
            {isEditing ? (
              <select
                name="gender"
                value={editedClient?.gender || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.gender ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.gender || 'N/A'}</p>
            )}
            {errors.gender && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Marital Status</p>
            {isEditing ? (
              <select
                name="marital_status"
                value={editedClient?.marital_status || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.marital_status ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              >
                <option value="">Select</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.marital_status || 'N/A'}</p>
            )}
            {errors.marital_status && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.marital_status}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Language</p>
            {isEditing ? (
              <div>
                <select
                  name="language"
                  value={editedClient?.language || ''}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border ${
                    errors.language ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  disabled={isLoadingLanguages}
                >
                  <option value="">Select Language</option>
                  {languages
                    .filter(lang => lang.active !== false) // Optional: only show active languages
                    .map((language) => (
                      <option key={language.id} value={language.name}>
                        {language.name}
                      </option>
                    ))}
                </select>
                
                {isLoadingLanguages && (
                  <p className="text-xs text-gray-500 mt-1">Loading languages...</p>
                )}
                {!isLoadingLanguages && languages.length === 0 && (
                  <p className="text-xs text-yellow-500 mt-1">No languages available</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.language || 'N/A'}</p>
            )}
            {errors.language && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.language}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">ID/Passport Number</p>
            {isEditing ? (
              <input
                type="text"
                name="id_passport_number"
                value={editedClient?.id_passport_number || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.id_passport_number ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.id_passport_number || 'N/A'}</p>
            )}
            {errors.id_passport_number && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.id_passport_number}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Mobile Number</p>
            {isEditing ? (
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  +27
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  className={`flex-1 block w-full rounded-none rounded-r-md border ${
                    mobileNumber && validateMobileNumber(mobileNumber) 
                      ? 'border-red-500' 
                      : 'border-gray-300'
                  } focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="8-10 digits"
                  maxLength={10}
                />
              </div>
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.phone || 'N/A'}</p> // ← CHANGE FROM mobile_number TO phone
            )}
            {mobileNumber && validateMobileNumber(mobileNumber) && isEditing && (
              <p className="text-red-500 text-xs mt-1">
                {validateMobileNumber(mobileNumber)}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Telephone Number</p>
            {isEditing ? (
              <input
                type="tel"
                name="telephone_number"
                value={editedClient?.telephone_number || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.telephone_number ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.telephone_number || 'N/A'}</p>
            )}
            {errors.telephone_number && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.telephone_number}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Tax Number</p>
            {isEditing ? (
              <input
                type="text"
                name="tax_number"
                value={editedClient?.tax_number || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.tax_number ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.tax_number || 'N/A'}</p>
            )}
            {errors.tax_number && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.tax_number}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Employer Name</p>
            {isEditing ? (
              <input
                type="text"
                name="employer_name"
                value={editedClient?.employer_name || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.employer_name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.employer_name || 'N/A'}</p>
            )}
            {errors.employer_name && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.employer_name}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Job Description</p>
            {isEditing ? (
              <input
                type="text"
                name="job_description"
                value={editedClient?.job_description || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.job_description ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.job_description || 'N/A'}</p>
            )}
            {errors.job_description && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.job_description}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Consultant</p>
            {isEditing ? (
              <div>
                <select
                  name="consultant"
                  value={editedClient?.consultant?.toString() || ''} // Ensure string comparison
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border ${
                    errors.consultant ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  disabled={isLoadingConsultants}
                >
                  <option value="">Select Consultant</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id.toString()}> {/* Ensure string value */}
                      {consultant.name} {consultant.surname}
                    </option>
                  ))}
                </select>

                {isLoadingConsultants && (
                  <p className="text-xs text-gray-500 mt-1">Loading consultants...</p>
                )}
                {!isLoadingConsultants && consultants.length === 0 && (
                  <p className="text-xs text-yellow-500 mt-1">No consultants available</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-900">
                {(() => {
                  if (!displayClient?.consultant_id) return 'N/A';
                  
                  // Find the consultant by ID - make sure we compare as strings
                  const consultant = consultants.find(c => 
                    c.id.toString() === displayClient.consultant_id?.toString()
                  );
                  
                  return consultant ? `${consultant.name} ${consultant.surname}` : 'Consultant not found';
                })()}
              </p>
            )}
            {errors.consultant && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.consultant}</p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700">Status</p>
            {isEditing ? (
              <select
                name="status"  // ← Changed to 'status'
                value={editedClient?.status || ''}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.status ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              >
                <option value="">Select Status</option>
                <option value="Draft">Draft</option>
                <option value="Quotation">Quotation</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            ) : (
              <p className="text-sm text-gray-900">{displayClient?.status || 'N/A'}</p>  // ← Changed to 'status'
            )}
            {errors.status && isEditing && (
              <p className="text-red-500 text-xs mt-1">{errors.status}</p>
            )}
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Created Date</p>
            <p className="text-sm text-gray-900">
              {displayClient?.created_date || displayClient?.created_at 
                ? formatDate(displayClient.created_date || displayClient.created_at)
                : 'N/A'
              }
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Updated Date</p>
            <p className="text-sm text-gray-900">
              {displayClient?.updated_date || displayClient?.updated_at 
                ? formatDate(displayClient.updated_date || displayClient.updated_at)
                : 'N/A'
              }
            </p>
          </div>

            {/* Bottom section - Driver's License and Email side by side */}
<div className="lg:col-span-3">
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Driver's License Codes - Left side (2/3 width) */}
    <div className="lg:col-span-2">
      <p className="text-sm font-medium text-gray-700 mb-2">Driver's License Code/s</p>
      {isEditing ? (
        <div className="space-y-3">
          {driverLicenses.length === 0 ? (
            <p className="text-gray-500 text-sm mb-2">No license codes added yet.</p>
          ) : (
            driverLicenses.map((license, index) => {
              const availableLicenseCodes = getAvailableLicenseCodes(index);
              
              return (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Code</label>
                    <select
                      value={license.code}
                      onChange={(e) => updateDriverLicense(index, 'code', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select License Code</option>
                      {availableLicenseCodes.map(code => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                      {license.code && !availableLicenseCodes.includes(license.code) && (
                        <option value={license.code} disabled>
                          {license.code} (selected)
                        </option>
                      )}
                    </select>
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Of First Issue</label>
                    <input
                      type="date"
                      value={license.dateOfFirstIssue}
                      onChange={(e) => updateDriverLicense(index, 'dateOfFirstIssue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  {/* Add Expires On field */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expires On</label>
                    <input
                      type="date"
                      value={license.expiresOn}
                      onChange={(e) => updateDriverLicense(index, 'expiresOn', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => removeDriverLicense(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 h-[42px]"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })
          )}
          
          <div className="flex justify-start pt-2">
            <button
              type="button"
              onClick={addDriverLicense}
              disabled={!canAddMoreLicenses()}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Add License Code
            </button>
          </div>
          
          {!canAddMoreLicenses() && driverLicenses.length > 0 && (
            <p className="text-sm text-gray-500">All available license codes have been added</p>
          )}
        </div>
      ) : (
        <div className="mt-1">
          {displayClient?.drivers_license_codes ? (
            <div className="flex flex-wrap gap-2">
              {parseDriverLicenses(displayClient.drivers_license_codes).map((license, index) => (
                <div 
                  key={index} 
                  className="bg-blue-100 border border-blue-200 text-blue-800 px-3 py-2 rounded-md text-sm flex items-center gap-2"
                >
                  <span className="font-bold">{license.code}</span>
                  {license.dateOfFirstIssue && (
                    <span className="text-blue-600 text-xs">
                      (Issued: {new Date(license.dateOfFirstIssue).toLocaleDateString()})
                    </span>
                  )}
                  {license.expiresOn && (
                    <span className="text-red-600 text-xs">
                      (Expires: {new Date(license.expiresOn).toLocaleDateString()})
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-900">N/A</p>
          )}
        </div>
      )}
      {errors.drivers_license_codes && isEditing && (
        <p className="text-red-500 text-xs mt-1">{errors.drivers_license_codes}</p>
      )}
    </div>

    {/* Email Addresses - Right side (1/3 width) */}
    <div>
      <p className="text-sm font-medium text-gray-700">Email Addresses</p>
      {isEditing ? (
        <div className="mt-1">
          {/* Email Input Field */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1">
              <input
                type="text"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onKeyDown={handleEmailKeyDown}
                placeholder="Enter email and press Enter or Add"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-gray-50 text-sm"
              />
              {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
            </div>
            <button
              type="button"
              onClick={addEmail}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors font-medium text-sm whitespace-nowrap"
              style={{ height: '40px' }}
            >
              Add
            </button>
          </div>
          
          {/* Display Added Emails */}
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
            {clientEmails.length === 0 ? (
              <p className="text-gray-500 text-sm">No email addresses added yet</p>
            ) : (
              clientEmails.map((email, index) => (
                <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-700 font-medium text-sm truncate">{email.email}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex-shrink-0">
                      {email.is_primary ? 'Primary' : 'Secondary'}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {!email.is_primary && (
                      <button
                        type="button"
                        onClick={() => setPrimaryEmail(index)}
                        className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                      >
                        Set Primary
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeEmail(index)}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Email Tips */}
          <div className="mt-3 text-xs text-gray-500 space-y-1">
            <p>• Press Enter or comma (,) to add email</p>
            <p>• First email added becomes primary</p>
            <p>• You can change which email is primary</p>
          </div>
        </div>
      ) : (
        <div className="mt-1">
          {clientEmails.length === 0 ? (
            <p className="text-sm text-gray-900">N/A</p>
          ) : (
            <div className="space-y-2">
              {clientEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-900 truncate">{email.email}</span>
                  {email.is_primary && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex-shrink-0">
                      Primary
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {errors.email && isEditing && (
        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
      )}
    </div>
  </div>
</div>

        </div>
      </form>
    </div>
  );
};

export default ClientInfoTab;